import React, { useState, useEffect } from 'react'
import styled from '@emotion/styled'
import { MenuCategory } from '@open-tender/types'
import { remsToNumber, slugify } from '@open-tender/utils'
import { NavArrowUp } from '../../icons'
import { useTheme } from '@emotion/react'

const CategoryNavView = styled.div`
  width: 100%;
  text-align: center;
  height: ${(props) => props.theme.layout.navHeight};
  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    text-align: left;
    height: ${(props) => props.theme.layout.navHeightMobile};
  }
  align-items: center;

  > ul {
    display: flex;
    position: relative;
    align-items: center;
    height: ${(props) => props.theme.layout.navHeight};
    justify-content: center;
    @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
      justify-content: flex-start;
      height: ${(props) => props.theme.layout.navHeightMobile};
    }

    > li {
      display: inline-table;
      font-size: ${(props) => props.theme.fonts.preface.fontSize};
      padding: 0 0 0 ${(props) => props.theme.layout.padding};
      @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
        padding-left: ${(props) => props.theme.layout.paddingMobile};
        padding-right: 1rem; 
      }

      &:last-child {
        padding-right: ${(props) => props.theme.layout.padding};
        @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
          padding-right: ${(props) => props.theme.layout.paddingMobile};
        }
      }
    }
  }
`

const CategoryNavButtonView = styled.button<{ active?: boolean }>`
  label: CategoryNavButtonView;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: ${(props) => props.theme.fonts.preface.family};
  font-weight: ${(props) => props.theme.fonts.preface.weight};
  font-style: ${(props) => props.theme.fonts.preface.fontStyle};
  letter-spacing: ${(props) => props.theme.fonts.preface.letterSpacing};
  text-transform: ${(props) => props.theme.fonts.preface.textTransform};
  -webkit-font-smoothing: ${(props) => props.theme.fonts.preface.fontSmoothing};
  font-size: ${(props) => props.theme.fonts.preface.fontSize};
  color: ${(props) =>
    props.theme.links.light[props.active ? 'hover' : 'color']};
  line-height: 20px;
  @media (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    font-size: ${(props) =>
      `${
        (remsToNumber(props.theme.fonts.preface.fontSize || '1.6rem') * 0.8) /
        10
      }rem`};
  }

  &:hover,
  &:focus {
    color: ${(props) => props.theme.links.light.hover};
  }
`

const SubCategoryView = styled.div<{ active?: boolean }>`
  position: fixed;
  display: flex;
  background-color: ${(props) => props.theme.bgColors.dark};
  padding: 5px;
  padding-right: 10px;
  padding-top: calc(${(props) => props.theme.layout.navHeight} / 2);
  
  > ul {
    display: inline-table;
    text-align: left;
    height: auto;

    > li {
      padding-bottom: 10px;
      padding-left: ${(props) => props.theme.layout.paddingMobile};
      color: ${(props) => props.theme.links.light[props.active ? 'hover' : 'color']};
      &:hover {
        color: ${(props) => props.theme.links.light['hover']};
        cursor: pointer;
        font-size: ${(props) => props.theme.fonts.preface.fontSize};
      }
    }
  }
`

const SubCategory = ({
  subcategory,
  setActiveSectionId,
  setActiveSectionName,
  setActiveSubSectionId,
  activeSubSectionId,
}: {
  subcategory: MenuCategory,
  setActiveSectionId: React.Dispatch<React.SetStateAction<string | number | null>>
  setActiveSectionName?: (name: string) => void
  setActiveSubSectionId: React.Dispatch<React.SetStateAction<string | number | null>>
  activeSubSectionId?: string | number | null
}) => {

  const handleSubCategoryClick = ((e: React.SyntheticEvent, subcategory: MenuCategory) => {
    e.preventDefault()
    e.stopPropagation()
    setActiveSectionId(null)
    setActiveSubSectionId(subcategory.id)
    const activeName = slugify(subcategory.name)
    if(setActiveSectionName) setActiveSectionName(activeName)
  })

  const theme = useTheme()

  return (
    <SubCategoryView>
      <ul>
        {subcategory.children.map((sub: MenuCategory, subIndex: number) => {
          return (
            <li
              key={`${sub.id}-${subIndex}`}
              onClick={(e) => handleSubCategoryClick(e, sub)}
              style={{ color: theme.links.light[activeSubSectionId === sub.id ? 'hover' : 'inherit'] }}
            >
              {sub.name}
            </li>
          )
        })}
    </ul>
    </SubCategoryView>
  )
}

const CategoryPopupMenu = ({
  categories,
  setActiveSection,
}: {
  categories: MenuCategory[]
  setActiveSection?: (name: string) => void
}) => {
  const [activeSectionId, setActiveSectionId] = useState<string | number | null>(null)
  const [activeSubSectionId, setActiveSubSectionId] = useState<string | number | null>(null)

  const handleCategoryClick = ((evt: React.SyntheticEvent, category: MenuCategory) => {
    evt.preventDefault()
    evt.stopPropagation()
    const activeId = activeSectionId === category.id ? null : category.id
    setActiveSectionId(activeId)
    if (category.children.length === 0) {
      setActiveSubSectionId(null)
      if (setActiveSection) setActiveSection(slugify(category.name))
    }
  })

  const theme = useTheme()

  useEffect(() => {
    // auto select first cagegory and subcategory if not already selected
    if (categories.length > 0 && activeSectionId === null) {
      let category = categories[0]
      if (category.children.length > 0 && activeSubSectionId === null) {
        setActiveSubSectionId(category.children[0].id)
        category = category.children[0]
      }
      if (activeSectionId === null && activeSubSectionId === null) {
        if (setActiveSection) setActiveSection(slugify(category.name))
      }
    }
    // just need to run this when categories change, not when activeSectionId or activeSubSectionId changes
  }, [categories])

  return (
    <CategoryNavView>
      <ul>
        {categories.map((category, index) => {
          const activeCategory = activeSectionId === category.id
          const hasChildren = category.children.length > 0
          const activeSubCategory = category.children.find((child) => child.id === activeSubSectionId) !== undefined
          const selectedCategoryOrSubcategory = activeSubCategory || activeCategory

          return (
            <li key={`${category.id}-${index}`}>
              <CategoryNavButtonView onClick={(e) => handleCategoryClick(e, category)}>
                <div style={{
                  color: theme.links.light[selectedCategoryOrSubcategory ? 'hover' : 'inherit']
                }}>{category.name}</div>
                {hasChildren && <div style={{
                    transform: selectedCategoryOrSubcategory ? 'translateY(-3px) rotate(180deg)' : '',
                    transition: 'transform 0.3s ease-in-out'
                  }}>
                    <NavArrowUp
                      color={theme.links.light[selectedCategoryOrSubcategory ? 'hover' : 'color']}
                    />
                </div>}
              </CategoryNavButtonView>
              {activeCategory && hasChildren &&
                <SubCategory
                  subcategory={category}
                  setActiveSectionId={setActiveSectionId}
                  setActiveSectionName={setActiveSection}
                  setActiveSubSectionId={setActiveSubSectionId}
                  activeSubSectionId={activeSubSectionId}
                />}
            </li>
          )
        })}
      </ul>
    </CategoryNavView>
  )
}

export default CategoryPopupMenu
